import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Lightbulb, Info, AlertTriangle, Search, ArrowRight, CheckCircle2, MinusCircle, FileText,
  BarChart3, ScrollText, Loader2,
} from "lucide-react";

const Card = ({ className = "", children, ...p }) => (
  <div className={`bg-white rounded-xl border border-brand-gold/25 shadow-[0_1px_2px_rgba(15,58,45,0.04)] ${className}`} {...p}>{children}</div>
);
const TONE = {
  PROBLEMA: { color: "#B0452E", icon: AlertTriangle },
  OPPORTUNITÀ: { color: "#2F6B4F", icon: Lightbulb },
  "DA VERIFICARE": { color: "#6F6A5F", icon: Search },
};

export function SectionHead({ icon: Icon, title, sub, right }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-3">
      <div className="flex items-center gap-2.5">
        <Icon size={20} className="text-brand-gold" strokeWidth={1.9} />
        <div><h3 className="font-serif text-[20px] text-brand-ink leading-none">{title}</h3>{sub && <p className="text-[12.5px] text-brand-muted mt-1">{sub}</p>}</div>
      </div>
      {right}
    </div>
  );
}

function Pillar({ p, i }) {
  const [open, setOpen] = useState(false);
  const rows = p.detail?.rows || [];
  const hasDetail = rows.length > 0 || !!p.detail?.note;
  return (
    <Card className={`p-5 flex flex-col ${p.soft ? "bg-[#F4F7F4]" : ""} ${open ? "md:col-span-2 xl:col-span-4" : ""}`} data-testid={`pillar-${i}`}>
      <div className="flex items-start gap-3 pb-3 border-b border-brand-gold/30">
        <span className="w-11 h-11 rounded-full bg-brand-green flex items-center justify-center shrink-0"><p.icon size={22} className="text-brand-gold" strokeWidth={1.7} /></span>
        <div><div className="font-serif text-[22px] md:text-[24px] text-brand-ink leading-none tracking-tight" data-testid={`pillar-${i}-title`}>{p.title}</div><p className="text-[12px] text-brand-muted mt-1.5 leading-relaxed">{p.text}</p></div>
      </div>
      <div className="mt-3 space-y-1.5 flex-1 min-w-0">
        {p.checks.map((c, j) => (
          <div key={j} className="flex items-start gap-2 text-[12.5px] text-brand-ink">
            {c.ok ? <CheckCircle2 size={15} className="text-emerald-700 shrink-0 mt-0.5" /> : <MinusCircle size={15} className="text-brand-orange shrink-0 mt-0.5" />}<span>{c.text}</span>
          </div>
        ))}
      </div>
      {hasDetail && (
        <button onClick={() => setOpen((o) => !o)} data-testid={`pillar-${i}-toggle`} className="mt-4 self-start inline-flex items-center justify-center gap-1.5 text-[12.5px] font-semibold text-brand-green border border-brand-green/30 rounded-lg px-3 py-2 hover:bg-brand-green hover:text-brand-cream transition-colors">
          {open ? "Chiudi" : "Approfondisci"} <ArrowRight size={13} className={open ? "rotate-90 transition-transform" : "transition-transform"} />
        </button>
      )}
      {open && (
        <div className="mt-4 border-t border-brand-gold/20 pt-4" data-testid={`pillar-${i}-detail`}>
          {p.detail.note && <p className="text-[12.5px] text-brand-muted mb-3">{p.detail.note}</p>}
          {rows.length > 0 && (
            <div className="overflow-auto">
              <table className="w-full text-[12.5px]">
                <thead><tr className="text-left text-[11px] uppercase tracking-wide text-brand-muted">{p.detail.columns.map((c, j) => <th key={j} className="pb-2 pr-4 font-semibold">{c}</th>)}</tr></thead>
                <tbody>{rows.map((r, j) => <tr key={j} className="border-t border-brand-gold/10">{r.map((cell, k) => <td key={k} className="py-1.5 pr-4 align-top text-brand-ink max-w-[360px] break-words">{cell === true ? <CheckCircle2 size={14} className="text-emerald-700" /> : cell === false ? <MinusCircle size={14} className="text-brand-orange" /> : cell}</td>)}</tr>)}</tbody>
              </table>
            </div>
          )}
          {p.detail.onReport && <button onClick={p.detail.onReport} className="mt-3 text-[12.5px] font-semibold text-brand-green inline-flex items-center gap-1 hover:underline">Vai alla sezione del referto <ArrowRight size={13} /></button>}
        </div>
      )}
    </Card>
  );
}

export default function ModuleShell({ images = [], id, overline, icon: Icon, headline, subtitle, quote, quoteSub, kpis = [],
  action, status, emerge = [], emergeTitle, pillars = [], pillarsTitle, pillarsSub, detailed, children, footer }) {
  return (
    <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-5 lg:py-7 max-w-[1320px] 2xl:max-w-[1560px] min-[2000px]:max-w-[1760px] leu-in" data-testid={`module-${id}`}>
      {/* 1. Header + citazione */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-[68px] h-[68px] rounded-full bg-brand-green flex items-center justify-center shrink-0">
            <Icon size={30} className="text-brand-gold" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-brand-orange font-bold tracking-[0.22em] text-[11px] uppercase">{overline}</div>
            <h2 className="font-serif text-[26px] sm:text-[30px] lg:text-[34px] leading-[1.1] text-brand-ink mt-1" data-testid={`module-${id}-headline`}>{headline}</h2>
            <p className="text-[14.5px] text-brand-muted mt-2 max-w-2xl leading-relaxed">{subtitle}</p>
          </div>
        </div>
        {quote && (
          <div className="rounded-xl bg-[#FBF8F1] border border-brand-gold/30 p-5 flex gap-3">
            <Lightbulb size={22} className="text-brand-gold shrink-0 mt-0.5" strokeWidth={1.6} />
            <div>
              <p className="font-serif italic text-[15px] text-brand-ink leading-snug">“{quote}”</p>
              <div className="w-10 border-t-2 border-brand-gold my-2.5" />
              {quoteSub && <p className="text-[12px] text-brand-muted">{quoteSub}</p>}
            </div>
          </div>
        )}
      </div>

      {/* 2. KPI */}
      {kpis.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6" data-testid={`module-${id}-kpis`}>
          {kpis.map((k, i) => (
            <Card key={i} className="p-4 flex items-center gap-3" data-testid={`kpi-${k.testid || i}`}>
              {k.icon && <k.icon size={26} className="shrink-0" style={{ color: k.color || "#C6902A" }} strokeWidth={1.6} />}
              <div className="min-w-0 flex-1">
                <div className={`font-serif leading-tight ${typeof k.value === "string" && k.value.length > 12 ? "text-[14px] capitalize" : "text-[26px] leading-none"}`} style={{ color: k.color === "#B0452E" ? k.color : "#211F1A" }}>{k.value}</div>
                <div className="text-[11.5px] text-brand-muted mt-1 leading-tight">{k.label}</div>
              </div>
              <Info size={13} className="text-brand-muted/60 shrink-0" title={k.hint} />
            </Card>
          ))}
        </div>
      )}

      {/* 3. Azione + stato */}
      {(action || status) && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4 mb-8">
          {action && (
            <div className="rounded-xl bg-brand-green text-brand-cream p-5 flex items-center gap-5" data-testid={`module-${id}-action`}>
              <div className="w-16 h-16 rounded-full border-2 border-brand-gold/70 flex items-center justify-center shrink-0">
                <action.icon size={28} className="text-brand-gold" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-[22px] leading-tight">{action.title}</div>
                <div className="text-[13px] text-brand-cream/75 mt-1">{action.sub}</div>
                {action.extra && <div className="mt-3">{action.extra}</div>}
                {action.cta && (
                  <Button data-testid={`module-${id}-cta`} onClick={action.onClick} disabled={action.busy}
                    className="mt-3 h-10 rounded-lg bg-brand-gold hover:bg-brand-goldSoft text-brand-greenDark text-[13px] font-semibold">
                    {action.busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : action.ctaIcon ? <action.ctaIcon size={15} className="mr-2" /> : null}{action.cta}
                  </Button>
                )}
              </div>
            </div>
          )}
          {status && (
            <Card className="p-5" data-testid={`module-${id}-status`}>
              <div className="text-[11px] font-bold uppercase tracking-wide text-brand-muted">Stato analisi</div>
              <div className="flex items-center gap-2 mt-2 text-[15px] font-semibold" style={{ color: status.ok ? "#2F6B4F" : "#C2662E" }}>
                {status.ok ? <CheckCircle2 size={18} /> : <MinusCircle size={18} />} {status.title}
              </div>
              {status.progress != null && (
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex-1 h-2.5 rounded-full bg-[#EDE7D8] overflow-hidden"><div className="h-full bg-brand-green rounded-full" style={{ width: `${status.progress}%` }} /></div>
                  <span className="font-serif text-[20px] text-brand-ink">{status.progress}%</span>
                </div>
              )}
              <div className="mt-2 space-y-0.5">{(status.lines || []).map((l, i) => <div key={i} className="text-[12.5px] text-brand-muted">{l}</div>)}</div>
            </Card>
          )}
        </div>
      )}

      {/* 4. Cosa emerge */}
      {emerge.length > 0 && (
        <section className="mb-8">
          <SectionHead icon={BarChart3} title={emergeTitle || "Cosa emerge adesso"} sub="Una sintesi rapida delle principali evidenze consolidate, con suggerimenti operativi." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {emerge.map((it, i) => {
              const t = TONE[it.tone] || TONE["DA VERIFICARE"];
              return (
                <Card key={i} className="overflow-hidden flex" data-testid={`emerge-${i}`}>
                  <div className="p-5 flex-1 min-w-0 flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: t.color }}><t.icon size={15} className="text-white" /></span>
                      <div><div className="text-[10.5px] font-bold tracking-[0.16em]" style={{ color: t.color }}>{it.tone}</div><div className="font-serif text-[16px] text-brand-ink leading-snug">{it.title}</div></div>
                    </div>
                    <p className="text-[12.5px] text-brand-muted mt-2.5 leading-relaxed flex-1">{it.text}</p>
                    {it.onClick && <button onClick={it.onClick} className="mt-3 self-start inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-brand-green border border-brand-green/30 rounded-lg px-3 py-1.5 hover:bg-brand-green hover:text-brand-cream transition-colors">Vedi dettagli <ArrowRight size={13} /></button>}
                  </div>
                  {(it.img || images[i % images.length]) && <img src={it.img || images[i % images.length]} alt="" className="w-[110px] object-cover shrink-0 hidden sm:block" />}
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* 5. Pilastri */}
      {pillars.length > 0 && (
        <section className="mb-8">
          <SectionHead icon={ScrollText} title={pillarsTitle || "Le aree analizzate"} sub={pillarsSub} />
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {pillars.map((p, i) => <Pillar key={i} p={p} i={i} />)}
          </div>
        </section>
      )}

      {children}

      {/* 6. Analisi dettagliata */}
      {detailed && (
        <section className="mb-8">
          <SectionHead icon={FileText} title="Analisi dettagliata" sub="Approfondisci con l'analisi completa di LEUTELIA. Ogni sezione contiene dati, interpretazioni e suggerimenti." />
          <Card className="p-5 flex flex-wrap items-center gap-5" data-testid={`module-${id}-detailed`}>
            {(detailed.img || images[0]) && <img src={detailed.img || images[0]} alt="" className="w-[180px] h-[100px] object-cover rounded-lg" />}
            <div className="flex-1 min-w-[240px]">
              <div className="font-serif text-[20px] text-brand-ink">{detailed.title} — Analisi completa</div>
              <p className="text-[13px] text-brand-muted mt-1">{detailed.text}</p>
              <div className="flex flex-wrap gap-4 mt-2 text-[12px] text-brand-muted">{(detailed.meta || []).map((m, i) => <span key={i}>· {m}</span>)}</div>
            </div>
            <div className="flex flex-col gap-2">
              <Button data-testid={`module-${id}-read-report`} disabled={!detailed.onReport} onClick={detailed.onReport} className="h-10 rounded-lg bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[13px] font-semibold disabled:opacity-60">Leggi il referto completo <ArrowRight size={14} className="ml-2" /></Button>
              <Button data-testid={`module-${id}-see-evidence`} variant="outline" disabled={!detailed.onEvidence} onClick={detailed.onEvidence} className="h-10 rounded-lg border-brand-green/30 text-brand-green text-[13px] font-semibold disabled:opacity-60">Vedi le prove <ArrowRight size={14} className="ml-2" /></Button>
              {detailed.status && <span className="text-[10.5px] text-brand-muted text-center" data-testid={`module-${id}-report-status`}>{detailed.status}</span>}
            </div>
          </Card>
        </section>
      )}
      {footer}
    </div>
  );
}
